import { Inject, Injectable } from '@angular/core';
import { HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, from, map, switchMap } from 'rxjs';
import { API_BASE_URL_SERVICE } from '../../../../../projects/kiene-core/src/lib/kiene-core.config';
import { KieneBackendApiService } from '../../../../../projects/kiene-core/src/lib/services/backend-api/kiene-backend-api.service';
import {
    WawiBulkEnqueueResult,
    WawiBulkReadyResult,
    WawiBulkStatusResult,
} from '../../../wa-wi/gemeinkosten/gemeinkosten-invoice-review/gemeinkosten-invoice-ki.service';
import { WawiInvoiceDraftDto } from '../../../wa-wi/gemeinkosten/gemeinkosten-invoice-review/gemeinkosten-invoice.models';

@Injectable({ providedIn: 'root' })
export class SonstigerErloesInvoiceKiService {
    private readonly base = 'invoice_ki/';
    private readonly persistTarget = 'sonstiger_erloes';

    constructor(
        private readonly api: KieneBackendApiService,
        @Inject(API_BASE_URL_SERVICE) private readonly apiBaseUrl: string,
    ) {}

    preview(file: File): Observable<WawiInvoiceDraftDto> {
        const form = new FormData();
        form.append('file', file, file.name);
        form.append('persistTarget', this.persistTarget);
        const options = { headers: new HttpHeaders('Phito-Type: fileupload') };
        return this.api.postFormData(this.apiBaseUrl + this.base + 'invoice_preview.php', form, options);
    }

    bulkEnqueue(files: File[]): Observable<WawiBulkEnqueueResult> {
        const form = new FormData();
        for (const f of files) {
            form.append('files[]', f, f.name);
        }
        form.append('persistTarget', this.persistTarget);
        const options = { headers: new HttpHeaders('Phito-Type: fileupload') };
        return this.api.postFormData(this.apiBaseUrl + this.base + 'invoice_bulk.php', form, options);
    }

    bulkStatus(batchId: string): Observable<WawiBulkStatusResult> {
        return this.api.get(
            this.apiBaseUrl + this.base + 'invoice_bulk_status.php?batchId=' + encodeURIComponent(batchId),
        );
    }

    bulkFailed(limit = 30): Observable<WawiBulkReadyResult> {
        return this.api.get(
            this.apiBaseUrl + this.base + 'invoice_bulk_failed.php?limit=' + encodeURIComponent(String(limit)),
        );
    }

    autoCommit(runId: string): Observable<{ sonstigerErloesId?: number }> {
        return this.api.post(this.apiBaseUrl + this.base + 'invoice_auto_commit.php', {
            runId,
            target: this.persistTarget,
        });
    }

    getDraft(runId: string): Observable<WawiInvoiceDraftDto> {
        return this.api.get(this.apiBaseUrl + this.base + 'invoice_get.php?runId=' + encodeURIComponent(runId));
    }

    getPdfFile(runId: string, fileName = 'sonstiger-erloes.pdf'): Observable<File> {
        return this.api
            .getBlob(this.apiBaseUrl + this.base + 'invoice_pdf.php', new HttpParams().set('runId', runId))
            .pipe(switchMap((blob: Blob) => this.blobToPdfFile(blob, fileName)));
    }

    getErloesPdfFile(sonstigerErloesId: number, fileName = 'sonstiger-erloes.pdf'): Observable<File> {
        return this.api
            .getBlob(
                this.apiBaseUrl + 'sonstiger_erloes/read_pdf.php',
                new HttpParams().set('sonstiger_erloes_id', String(sonstigerErloesId)),
            )
            .pipe(switchMap((blob: Blob) => this.blobToPdfFile(blob, fileName)));
    }

    private blobToPdfFile(blob: Blob, fileName: string): Observable<File> {
        if (!blob || blob.size < 5 || (blob.type && blob.type.includes('json'))) {
            throw new Error('PDF-Antwort ungültig');
        }
        return from(blob.slice(0, 5).arrayBuffer()).pipe(
            map(buf => {
                const magic = String.fromCharCode(...new Uint8Array(buf));
                if (magic !== '%PDF-') {
                    throw new Error('PDF-Antwort ungültig (kein PDF-Header)');
                }
                return new File([blob], fileName, { type: 'application/pdf' });
            }),
        );
    }
}
